import { isValidElement } from 'react';
import { TableCellContent } from '../TableCellContent';
import type { GridColumnWidthModel } from './gridState';
import type { SxProps, Theme } from '@/ui/primitives';
import type { GridColDef, GridRenderCellParams, GridValidRowModel } from '@/ui/primitives';
import type { ReactNode } from 'react';

const resolveAlignmentSx = (
  align?: GridColDef<GridValidRowModel>['align'],
): SxProps<Theme> | undefined => {
  if (align === 'center') {
    return { justifyContent: 'center', textAlign: 'center' };
  }
  if (align === 'right') {
    return { justifyContent: 'flex-end', textAlign: 'right' };
  }
  return undefined;
};

const isSuiteOrTestBreakdownColumn = <T extends GridValidRowModel>(
  column: GridColDef<T>,
): boolean => {
  const headerName = typeof column.headerName === 'string' ? column.headerName.toLowerCase() : '';
  return headerName.includes('suite') || headerName.includes('test');
};

export const resolveGridColumnAlignment = <T extends GridValidRowModel>(
  column: GridColDef<T>,
): {
  align: NonNullable<GridColDef<T>['align']>;
  headerAlign: NonNullable<GridColDef<T>['headerAlign']>;
} => {
  if (isSuiteOrTestBreakdownColumn(column)) {
    return {
      align: column.align ?? 'center',
      headerAlign: column.headerAlign ?? 'center',
    };
  }

  return {
    align: 'left' as const,
    headerAlign: 'left' as const,
  };
};

type SyncReactNode = Exclude<ReactNode, Promise<unknown>>;

type TableColumnMeta = { cellPadding?: 'default' | 'compact' | 'none'; cellSx?: SxProps<Theme> };

const isTableCellContent = (node: ReactNode): boolean =>
  isValidElement(node) && node.type === TableCellContent;

const normaliseCellValue = (value: unknown): ReactNode => {
  if (isValidElement(value)) return value;
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'bigint' || typeof value === 'symbol') {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => {
        if (entry === null || entry === undefined) return null;
        if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
          return String(entry);
        }
        if (typeof entry === 'bigint' || typeof entry === 'symbol') {
          return String(entry);
        }
        if (entry instanceof Date) {
          return entry.toISOString();
        }
        return null;
      })
      .filter((entry): entry is string => Boolean(entry));
    return parts.join(', ');
  }
  return '';
};

const resolveCellContent = <T extends GridValidRowModel>(
  renderCell: GridColDef<T>['renderCell'],
  params: GridRenderCellParams<T, unknown>,
): ReactNode => {
  if (renderCell) {
    const rendered = renderCell(params);
    if (rendered !== undefined) {
      return rendered;
    }
  }
  return normaliseCellValue(params.formattedValue ?? params.value ?? '');
};

type WrapDataGridColumnsOptions<T extends GridValidRowModel> = {
  getBaseCellSx?: (params: GridRenderCellParams<T, unknown>) => SxProps<Theme> | undefined;
  columnWidthModel?: GridColumnWidthModel;
};

export const normalizeGridColumns = <T extends GridValidRowModel>(
  columns: Array<GridColDef<T>>,
): Array<GridColDef<T>> =>
  columns.map((column) => {
    const width = column.width;
    const hasWidth = typeof width === 'number';
    const hasMinWidth = typeof column.minWidth === 'number';
    const shouldAutoFlex = !hasWidth && !hasMinWidth && column.flex === undefined;
    const resolvedFlex = hasWidth ? undefined : shouldAutoFlex ? 1 : column.flex;
    return {
      ...column,
      flex: resolvedFlex,
      minWidth: column.minWidth,
      width: hasWidth ? width : undefined,
      maxWidth: column.maxWidth,
    };
  });

export const applyColumnWidths = <T extends GridValidRowModel>(
  columns: Array<GridColDef<T>>,
  columnWidthModel: GridColumnWidthModel | null | undefined,
): Array<GridColDef<T>> => {
  if (!columnWidthModel) return columns;
  const next = columns.map((column) => {
    const width = columnWidthModel[column.field];
    if (typeof width !== 'number') return column;
    return { ...column, width, flex: undefined };
  });
  const hasUpdates = next.some((column, index) => column !== columns[index]);
  return hasUpdates ? next : columns;
};

const resolveCellPaddingSx = (
  _cellPadding?: TableColumnMeta['cellPadding'],
): SxProps<Theme> | undefined => {
  return undefined;
};

export const wrapDataGridColumns = <T extends GridValidRowModel>(
  columns: Array<GridColDef<T>>,
  options?: WrapDataGridColumnsOptions<T>,
): Array<GridColDef<T>> =>
  normalizeGridColumns(applyColumnWidths(columns, options?.columnWidthModel)).map((column) => {
    const alignment = resolveGridColumnAlignment(column);
    const renderCell = column.renderCell;
    const wrappedRenderCell: GridColDef<T>['renderCell'] = (params) => {
      const baseSx = options?.getBaseCellSx?.(params);
      const alignmentSx = resolveAlignmentSx(alignment.align);
      const columnMeta = column as TableColumnMeta;
      const paddingSx = resolveCellPaddingSx(columnMeta.cellPadding);
      const resolvedSx = [baseSx, alignmentSx, paddingSx, columnMeta.cellSx].filter(
        Boolean,
      ) as Array<SxProps<Theme>>;
      const cellSx = resolvedSx.length > 0 ? (resolvedSx as SxProps<Theme>) : undefined;
      const content = resolveCellContent(renderCell, params) as SyncReactNode;
      if (isTableCellContent(content)) {
        return content;
      }
      return <TableCellContent sx={cellSx}>{content}</TableCellContent>;
    };
    return { ...column, ...alignment, renderCell: wrappedRenderCell };
  });
